"use client";

import { UserProfile } from "@/types";
import { useState } from "react";
import { motion } from "framer-motion";
import { User, Scale, Ruler, Activity, Target, ArrowRight, Heart } from "lucide-react";
import clsx from "clsx";
import Logo from "./Logo";
import { GOAL_CATEGORIES, GOAL_OPTIONS } from "@/data/goals";

interface OnboardingProps {
    onComplete: (profile: UserProfile) => void;
}

export default function Onboarding({ onComplete }: OnboardingProps) {
    const [step, setStep] = useState(1);
    const [profile, setProfile] = useState<UserProfile>({
        id: "",
        name: "",
        mobile: "",
        weight: 70,
        height: 175,
        age: 30,
        gender: "Male",
        activityLevel: "Moderate",
        goal: ""
    });

    const handleChange = (field: keyof UserProfile, value: string | number) => {
        setProfile((prev) => ({ ...prev, [field]: value }));
    };

    const isStepValid = () => {
        if (step === 1) {
            return profile.name.trim().length > 0 && profile.weight > 0 && profile.height > 0 && profile.age > 0;
        }
        if (step === 2) {
            return !!profile.gender && !!profile.activityLevel;
        }
        if (step === 3) {
            return profile.goal.trim().length > 0;
        }
        return false;
    };

    const handleNext = () => {
        if (!isStepValid()) return;

        if (step < 3) {
            setStep(step + 1);
        } else {
            onComplete(profile);
        }
    };

    return (
        <div className="min-h-screen bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-slate-900 via-[#020617] to-[#020617] flex flex-col items-center justify-center p-6 text-white overflow-hidden relative">
            {/* Background Effects */}
            <div className="absolute top-0 left-0 w-full h-96 bg-primary/5 blur-3xl pointer-events-none" />

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="w-full max-w-md relative z-10"
            >
                {/* Header */}
                <div className="text-center mb-10">
                    <Logo size="lg" />
                    <p className="text-text-muted mt-2">Let&apos;s verify your biomarkers for accurate tracking.</p>
                </div>

                {/* Form Steps */}
                <div className="glass-card p-6 mb-8 min-h-[320px] flex flex-col">
                    {step === 1 && (
                        <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            className="space-y-6 flex-1"
                        >
                            <h2 className="text-lg font-bold flex items-center gap-2">
                                <span className="w-6 h-6 rounded-full bg-primary/20 text-primary text-xs flex items-center justify-center border border-primary/20">1</span>
                                Basic Stats
                            </h2>

                            <div className="space-y-4">
                                <div>
                                    <label className="text-[10px] uppercase tracking-wider text-text-muted font-bold flex items-center gap-1 mb-2">
                                        <User size={12} /> Name
                                    </label>
                                    <input
                                        type="text"
                                        value={profile.name}
                                        onChange={(e) => handleChange("name", e.target.value)}
                                        placeholder="Enter your name"
                                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-primary/50 transition-colors placeholder:text-text-muted/50"
                                    />
                                </div>
                                <div>
                                    <label className="text-[10px] uppercase tracking-wider text-text-muted font-bold flex items-center gap-1 mb-2">
                                        <Scale size={12} /> Weight (kg)
                                    </label>
                                    <input
                                        type="number"
                                        value={profile.weight}
                                        onChange={(e) => handleChange("weight", parseFloat(e.target.value) || 0)}
                                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-primary/50 transition-colors"
                                    />
                                </div>
                                <div>
                                    <label className="text-[10px] uppercase tracking-wider text-text-muted font-bold flex items-center gap-1 mb-2">
                                        <Ruler size={12} /> Height (cm)
                                    </label>
                                    <input
                                        type="number"
                                        value={profile.height}
                                        onChange={(e) => handleChange("height", parseFloat(e.target.value) || 0)}
                                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-primary/50 transition-colors"
                                    />
                                </div>
                                <div>
                                    <label className="text-[10px] uppercase tracking-wider text-text-muted font-bold flex items-center gap-1 mb-2">
                                        <Heart size={12} /> Age
                                    </label>
                                    <input
                                        type="number"
                                        value={profile.age}
                                        onChange={(e) => handleChange("age", parseFloat(e.target.value) || 0)}
                                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-primary/50 transition-colors"
                                    />
                                </div>
                            </div>
                        </motion.div>
                    )}

                    {step === 2 && (
                        <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            className="space-y-6 flex-1"
                        >
                            <h2 className="text-lg font-bold flex items-center gap-2">
                                <span className="w-6 h-6 rounded-full bg-primary/20 text-primary text-xs flex items-center justify-center border border-primary/20">2</span>
                                Lifestyle
                            </h2>

                            <div className="space-y-4">
                                <div>
                                    <label className="text-[10px] uppercase tracking-wider text-text-muted font-bold flex items-center gap-1 mb-2">
                                        <User size={12} /> Gender
                                    </label>
                                    <div className="grid grid-cols-2 gap-3">
                                        {["Male", "Female"].map((g) => (
                                            <button
                                                key={g}
                                                onClick={() => handleChange("gender", g)}
                                                className={clsx(
                                                    "px-4 py-3 rounded-xl text-sm font-medium border transition-all",
                                                    profile.gender === g
                                                        ? "bg-blue-500/20 border-blue-500/50 text-blue-200"
                                                        : "bg-white/5 border-white/10 text-text-muted hover:bg-white/10"
                                                )}
                                            >
                                                {g}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div>
                                    <label className="text-[10px] uppercase tracking-wider text-text-muted font-bold flex items-center gap-1 mb-2">
                                        <Activity size={12} /> Activity
                                    </label>
                                    <div className="grid grid-cols-1 gap-2">
                                        {["Sedentary", "Moderate", "Very Active"].map((level) => (
                                            <button
                                                key={level}
                                                onClick={() => handleChange("activityLevel", level)}
                                                className={clsx(
                                                    "px-4 py-3 rounded-xl text-sm font-medium border text-left transition-all",
                                                    profile.activityLevel === level
                                                        ? "bg-emerald-500/20 border-emerald-500/50 text-emerald-200"
                                                        : "bg-white/5 border-white/10 text-text-muted hover:bg-white/10"
                                                )}
                                            >
                                                {level}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    )}

                    {step === 3 && (
                        <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            className="space-y-6 flex-1"
                        >
                            <h2 className="text-lg font-bold flex items-center gap-2">
                                <span className="w-6 h-6 rounded-full bg-primary/20 text-primary text-xs flex items-center justify-center border border-primary/20">3</span>
                                Goal
                            </h2>

                            <div>
                                <label className="text-[10px] uppercase tracking-wider text-text-muted font-bold flex items-center gap-1 mb-2">
                                    <Target size={12} /> Your Primary Goal
                                </label>
                                <div className="relative">
                                    <select
                                        value={profile.goal}
                                        onChange={(e) => handleChange("goal", e.target.value)}
                                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white appearance-none focus:outline-none focus:border-primary/50 transition-colors cursor-pointer"
                                    >
                                        <option value="" disabled className="bg-slate-900 text-gray-500">Select a goal...</option>
                                        {GOAL_CATEGORIES.map(category => (
                                            <optgroup key={category} label={category} className="bg-slate-900 text-white">
                                                {GOAL_OPTIONS.filter(g => g.Category === category).map(option => (
                                                    <option key={option.Goal} value={option.Goal}>
                                                        {option.Goal}
                                                    </option>
                                                ))}
                                            </optgroup>
                                        ))}
                                    </select>
                                    <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-text-muted">
                                        <Target size={14} />
                                    </div>
                                </div>
                                <p className="text-xs text-text-muted mt-3 leading-relaxed">
                                    This helps our AI Coach tailor your daily feedback and nutrition targets.
                                </p>
                            </div>
                        </motion.div>
                    )}
                </div>

                {/* Actions */}
                <button
                    onClick={handleNext}
                    disabled={!isStepValid()}
                    className={clsx(
                        "w-full font-bold uppercase tracking-widest text-sm py-4 rounded-xl shadow-lg transition-all flex items-center justify-center gap-2",
                        isStepValid()
                            ? "bg-primary text-black shadow-primary/25 hover:shadow-primary/40 hover:scale-[1.02] active:scale-[0.98]"
                            : "bg-white/10 text-white/40 cursor-not-allowed"
                    )}
                >
                    {step < 3 ? "Next Step" : "Get Started"}
                    <ArrowRight size={16} className={clsx("transition-transform", isStepValid() && "group-hover:translate-x-1")} />
                </button>

                {step > 1 && (
                    <button
                        onClick={() => setStep(step - 1)}
                        className="w-full mt-4 text-xs text-text-muted hover:text-white transition-colors"
                    >
                        Back
                    </button>
                )}
            </motion.div>
        </div>
    );
}
